import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, FormGroup, Label, Input, Button, Alert, Table } from 'reactstrap'
import { getIdData, addData, updateData } from '../../lib/collegerank'
import { getDataWithTypeForSelect } from '../../lib/parameters'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

const AdminCollegeEdit = () => {
	const navigate = useNavigate()
	const { id, layout } = useParams()
	const role = useRoleBasedUrl()

	// Check if we're in create mode by looking at the current path
	const currentPath = window.location.pathname
	const isAddMode = currentPath.includes('/create/')

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [collegeName, setCollegeName] = useState('')
	const [parameters, setParameters] = useState([])
	const [ranks, setRanks] = useState({})
	const [isDarkMode, setIsDarkMode] = useState(false)

	useEffect(() => {
		if (!isAddMode && id) {
			fetchCollegeData()
		} else {
			fetchParameterData()
		}
	}, [id, isAddMode])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])

	const fetchParameterData = async () => {
		try {
			setLoading(true)
			const parameterData = await getDataWithTypeForSelect('DESIRED_COLLEGES')
			setParameters(parameterData)
		} catch (err) {
			console.error('Error fetching parameters:', err)
			setError('Failed to load parameters')
		} finally {
			setLoading(false)
		}
	}

	const fetchCollegeData = async () => {
		if (!id || id === 'undefined') {
			return
		}

		try {
			setLoading(true)
			const collegeData = await getIdData(id)
			if (collegeData && collegeData.length > 0) {
				setCollegeName(collegeData[0].collegename || '')
				const rankObj = JSON.parse(collegeData[0].rankvalue || '{}')
				setRanks(rankObj)
			}
			await fetchParameterData()
		} catch (err) {
			console.error('Error fetching college data:', err)
			setError('Failed to load college data')
		} finally {
			setLoading(false)
		}
	}

	const getRank = (key) => {
		if (ranks && ranks[key] !== undefined) {
			return ranks[key]
		}
		return 0
	}

	const handleRankChange = (paramValue, rankValue) => {
		setRanks((prev) => ({
			...prev,
			[paramValue]: parseInt(rankValue)
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setError(null)

		if (!collegeName || collegeName.trim() === '') {
			setError('Please enter College Name')
			setLoading(false)
			return
		}

		try {
			if (isAddMode) {
				await addData({
					collegename: collegeName,
					rankvalue: ranks
				})
			} else {
				await updateData({
					id: id,
					collegename: collegeName,
					rankvalue: ranks
				})
			}
			navigate(`${process.env.PUBLIC_URL}/${role}/colleges/${layout}`)
		} catch (err) {
			console.error('Error saving college:', err)
			setError('Failed to save college data')
		} finally {
			setLoading(false)
		}
	}

	const handleCancel = () => {
		navigate(`${process.env.PUBLIC_URL}/${role}/colleges/${layout}`)
	}

	if (loading && !isAddMode) {
		return (
			<Container fluid>
				<Row>
					<Col sm="12">
						<Card style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
							<CardBody style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
								<div className="text-center">
									<div className="spinner-border" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
									<p className="mt-2" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Loading college data...</p>
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		)
	}

	return (
		<Container fluid>
			<Row>
				<Col sm="12">
					<Card style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
						<CardBody style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
							<div className="d-flex justify-content-between align-items-center mb-4">
								<h3 style={{ color: isDarkMode ? '#fff' : 'inherit' }}>{isAddMode ? 'Add College' : 'Update College'}</h3>
							</div>

							{error && (
								<Alert color="danger" className="mb-4">
									{error}
								</Alert>
							)}

							<form onSubmit={handleSubmit}>
								<Row>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>College Name</Label>
											<Input
												type="text"
												value={collegeName}
												onChange={(e) => setCollegeName(e.target.value)}
												placeholder="Enter College Name"
												required
												disabled={loading}
												style={{
													backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
													color: isDarkMode ? '#fff' : 'inherit'
												}}
											/>
										</FormGroup>
									</Col>
								</Row>

								<Row>
									<Col md="12">
										<FormGroup>
											<Label className="form-control-label" style={{ color: isDarkMode ? '#fff' : 'inherit' }}>College Rankings</Label>
											<Table responsive style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff', color: isDarkMode ? '#fff' : 'inherit' }}>
												<thead>
													<tr>
														<th style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Type</th>
														<th style={{ color: isDarkMode ? '#fff' : 'inherit' }}>Rank</th>
													</tr>
												</thead>
												<tbody>
													{parameters &&
														parameters.map((param) => (
															<tr key={param.value} style={{ backgroundColor: isDarkMode ? '#2a2d35' : '#fff' }}>
																<td style={{ color: isDarkMode ? '#fff' : 'inherit' }}>{param.label}</td>
																<td>
																	<Input
																		type="select"
																		value={getRank(param.value)}
																		onChange={(e) => handleRankChange(param.value, e.target.value)}
																		disabled={loading}
																		style={{
																			width: '100px',
																			backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
																			color: isDarkMode ? '#fff' : 'inherit'
																		}}
																	>
																		<option value={0}>0</option>
																		<option value={1}>1</option>
																		<option value={2}>2</option>
																		<option value={3}>3</option>
																	</Input>
																</td>
															</tr>
														))}
												</tbody>
											</Table>
										</FormGroup>
									</Col>
								</Row>

								<div className="d-flex justify-content-between mt-4">
									<Button type="submit" color="primary" disabled={loading}>
										{loading ? 'Saving...' : isAddMode ? 'Add College' : 'Update College'}
									</Button>
									<Button type="button" color="secondary" onClick={handleCancel} disabled={loading}>
										Cancel
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</Container>
	)
}

export { AdminCollegeEdit }
